import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Modal from "react-bootstrap/Modal";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Rulers } from "react-bootstrap-icons";

import { profileIsComplete } from "./wizardSteps";

/**
 * The one time the wizard asks to be used.
 *
 * A bowler with no sheet at all has just been created, and that is the moment
 * guided measuring is worth offering. A shop full of veterans can turn the
 * offer off for good from here, and the header button stays either way --
 * being asked on every new customer is how a helpful thing becomes a nuisance.
 *
 * It waits for the name and phone first. A brand new bowler opens with the
 * profile editor already up, because the header opens it whenever the record
 * is incomplete, and asking about measuring over the top of that is asking a
 * second question before the first one is answered.
 */

const NEVER_ASK = "spectre_wizard_never_ask";

const StyledModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: ${(p) => p.theme.radius.lg};
    box-shadow: inset 0 4px 0 ${(p) => p.theme.color.primary},
      0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }

  .modal-header,
  .modal-footer {
    border: none;
  }

  .modal-footer {
    justify-content: space-between;
    gap: ${(p) => p.theme.spacing.sm};
  }
`;

const Title = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeLg};
  font-weight: ${(p) => p.theme.font.weightBold};
  color: ${(p) => p.theme.color.primary};
`;

const Body = styled.div`
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  color: ${(p) => p.theme.color.text};
`;

const Button = styled.button`
  min-height: 44px;
  padding: 8px 18px;
  border-radius: ${(p) => p.theme.radius.md};
  border: 1px solid
    ${(p) => (p.$primary ? p.theme.color.primary : p.theme.color.border)};
  background: ${(p) =>
    p.$primary ? p.theme.color.primary : p.theme.color.surface};
  color: ${(p) => (p.$primary ? "#ffffff" : p.theme.color.primary)};
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeMd};
  font-weight: ${(p) => p.theme.font.weightSemibold};

  &:hover {
    background: ${(p) =>
      p.$primary ? p.theme.color.primaryDark : "rgba(0, 24, 126, 0.08)"};
  }
`;

const Never = styled.button`
  border: none;
  background: transparent;
  padding: 8px 4px;
  font-family: ${(p) => p.theme.font.family};
  font-size: ${(p) => p.theme.font.sizeSm};
  color: ${(p) => p.theme.color.textMuted};
  text-decoration: underline;
`;

const Buttons = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.sm};
`;

const WizardOfferComponent = ({
  onAccept,
  specsheets,
  isReadOnly,
  offerEnabled,
  profileReady,
}) => {
  const { t } = useTranslation();
  const [answered, setAnswered] = useState(false);

  const eligible =
    offerEnabled &&
    profileReady &&
    !isReadOnly &&
    specsheets.length === 0 &&
    !answered;

  // Asked once per browser, not once per bowler: an operator who said no is
  // saying no to the offer, not to this customer.
  const [muted, setMuted] = useState(
    () => window.localStorage.getItem(NEVER_ASK) === "1",
  );

  useEffect(() => {
    if (specsheets.length > 0) setAnswered(true);
  }, [specsheets.length]);

  if (!eligible || muted) return null;

  return (
    <StyledModal show centered onHide={() => setAnswered(true)}>
      <Modal.Header closeButton>
        <Title>
          <Rulers /> {t("wizard.offer.title", "Measure this bowler?")}
        </Title>
      </Modal.Header>
      <Modal.Body>
        <Body>
          {t(
            "wizard.offer.body",
            "This bowler has no spec sheet yet. Guided measuring walks through the fitting step by step and fills the sheet as it goes.",
          )}
        </Body>
      </Modal.Body>
      <Modal.Footer>
        <Never
          type="button"
          onClick={() => {
            window.localStorage.setItem(NEVER_ASK, "1");
            setMuted(true);
          }}
        >
          {t("wizard.offer.never", "Don't ask again")}
        </Never>
        <Buttons>
          <Button type="button" onClick={() => setAnswered(true)}>
            {t("wizard.offer.later", "Not now")}
          </Button>
          <Button
            type="button"
            $primary
            onClick={() => {
              setAnswered(true);
              onAccept();
            }}
          >
            {t("wizard.action.open", "Guided measuring")}
          </Button>
        </Buttons>
      </Modal.Footer>
    </StyledModal>
  );
};

export const WizardOffer = connect((state) => ({
  specsheets: state.specsheet.specsheets,
  isReadOnly: state.userSession.isReadOnly,
  offerEnabled:
    state.settings.appSettings.autoSuggestions.spec_sheet_wizard_offer !== false,
  profileReady: profileIsComplete(state.bowlers.selectedCustomer, {
    requireEmail: state.settings.appSettings.bowlerPreferences?.require_email,
    requirePhone: state.settings.appSettings.bowlerPreferences?.require_phone,
  }),
}))(WizardOfferComponent);

export default WizardOffer;
